/**
 * Splice a reordered subset of categories back into the full per-store aisle
 * order (ARCHITECTURE §6.5).
 *
 * The aisle-order screen has two scopes. In "all" scope it edits the whole
 * order and saves it directly. In "list" scope — reached from the list, in a
 * shop — it shows only the categories that are actually on the list for that
 * store, so reordering the handful that matter is a ten-second job rather than a
 * hunt through ~25. This is what merges that short reorder back into the order
 * of everything else.
 *
 * `fullOrder` is every category the household buys for this store, already
 * resolved to a total order (positioned ones by position, the rest by name —
 * exactly what the screen's `known` computes). `subsetOrder` is those of them
 * the list touches, in the new order the person just arranged; it must be a
 * permutation of the `fullOrder` entries that appear in it.
 *
 * **Slot-fill, not reinsert.** Categories the list does not touch keep their
 * absolute slot; the touched ones are dealt back into the slots they
 * *collectively* occupied, in their new relative order. So a list category
 * reshuffles within the positions the list already spanned — it never leaps
 * past a category the person did not see. That is the precise reading of
 * "reorder relative to the supermarket, keep the total order correct":
 *
 *   full:     [groente, zuivel, brood, vlees, diepvries, snacks]
 *   on list:  zuivel, diepvries, snacks           (slots 1, 4, 5)
 *   reordered: diepvries, zuivel, snacks
 *   result:   [groente, diepvries, brood, vlees, zuivel, snacks]
 *
 * groente/brood/vlees never move; the three list categories now read
 * diepvries → zuivel → snacks.
 *
 * If the subset is not a clean permutation of its members in `fullOrder` — a
 * caller bug, since the screen derives it by filtering `fullOrder` — this
 * returns the full order untouched rather than producing a scrambled one. A
 * no-op is the safe degradation; a corrupted order would be shared to the other
 * phone.
 */
export function spliceAisleOrder(fullOrder: string[], subsetOrder: string[]): string[] {
  const inSubset = new Set(subsetOrder);

  // Guard: every subset member must appear in the full order, exactly as many
  // times as the subset expects. Anything else and we cannot slot-fill safely.
  const hits = fullOrder.filter((category) => inSubset.has(category)).length;
  if (inSubset.size !== subsetOrder.length || hits !== subsetOrder.length) {
    return [...fullOrder];
  }

  const result = [...fullOrder];
  let next = 0;
  for (let i = 0; i < result.length; i++) {
    if (inSubset.has(result[i]!)) result[i] = subsetOrder[next++]!;
  }
  return result;
}
